import { Mic, Play, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { player } from '../audio/player';
import { micAccess, startRecording, type MicAccess, type RecordingHandle, type RecordingResult } from '../audio/recorder';
import { micNoteText } from './MicNote';

const STATUS: Record<MicAccess, string> = {
  granted: 'Allowed for this site',
  prompt: "Not tried yet: you'll be asked the first time",
  denied: 'Blocked for this site',
  unsupported: "This browser can't record",
  insecure: 'Needs a secure (https) connection',
};

/** Settings › Microphone: what the browser allows, and a quick record-and-play-back check. */
export function MicTest() {
  const [access, setAccess] = useState<MicAccess | null>(null);
  const [phase, setPhase] = useState<'idle' | 'recording' | 'done' | 'failed'>('idle');
  const [result, setResult] = useState<RecordingResult | null>(null);
  const handle = useRef<RecordingHandle | null>(null);
  const level = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    micAccess().then(setAccess);
    return () => handle.current?.cancel();
  }, []);

  useEffect(() => () => {
    if (result) URL.revokeObjectURL(result.url);
  }, [result]);

  const test = async () => {
    if (phase === 'recording') {
      handle.current?.stop();
      return;
    }
    player.prime();
    try {
      const h = await startRecording({
        autoStop: true,
        onLevel: (l) => level.current?.style.setProperty('width', `${Math.round(l * 100)}%`),
      });
      handle.current = h;
      setPhase('recording');
      const r = await h.result;
      handle.current = null;
      setAccess(await micAccess());
      if (r) {
        setResult(r);
        setPhase('done');
        player.play(r.url, { ...r.speech, tag: 'mic-test' });
      } else {
        setPhase('failed');
      }
    } catch {
      setAccess(await micAccess());
      setPhase('failed');
    }
  };

  const note = micNoteText(access);
  const usable = access !== 'unsupported' && access !== 'insecure';
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="setting" style={{ padding: 0, minHeight: 0 }}>
        <div className="setting-text">
          <span className="setting-label">Microphone</span>
          <span className="setting-help" aria-live="polite">
            {access ? STATUS[access] : 'Checking…'}
          </span>
        </div>
        {usable && (
          <button type="button" className="btn btn-secondary" onClick={test}>
            {phase === 'recording' ? <Square size={16} aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
            {phase === 'recording' ? 'Stop' : 'Test it'}
          </button>
        )}
      </div>
      {phase === 'recording' && (
        <div className="stack" style={{ gap: 6 }}>
          <p className="small">Say something, then pause. It plays back by itself.</p>
          <div className="match-bar" aria-hidden="true">
            <span ref={level} style={{ width: '0%', transition: 'width 80ms linear' }} />
          </div>
        </div>
      )}
      {phase === 'done' && result && (
        <p className="small row" style={{ gap: 8 }}>
          <button type="button" className="btn btn-ghost small" style={{ marginLeft: -12 }} onClick={() => player.play(result.url, { ...result.speech, tag: 'mic-test' })}>
            <Play size={16} aria-hidden="true" fill="currentColor" /> Play it again
          </button>
          <span className="muted">The microphone works.</span>
        </p>
      )}
      {phase === 'failed' && <p className="small">The test didn't record anything.</p>}
      {note && (phase === 'failed' || !usable || access === 'denied') && <p className="small muted">{note}</p>}
    </div>
  );
}
